'use client'

import { sanitizeHTML } from '@/lib/utils'
import Link from 'next/link'
import { useScrollAnimation } from '@/hooks/useScrollAnimation'
import FadeIn from '@/components/ui/FadeIn'
import Image from 'next/image'
import { getReadableImageUrl } from '@/lib/directus'
import { useState } from 'react'

interface ImageSplitDarkTextData {
	anchor_title?: string
	heading?: {
		text?: string
		title?: string
	}
	text?: {
		text?: string
	}
	button?: {
		href: string
		label: string
	}
	image?: {
		alt: string
		image_file: {
			image: {
				id: string
				filename_disk: string
			}
		}
	}
	options_page?: string[]
	video?: {
		embed_url?: string
		embed_title?: string
		photo?: {
			id?: string
			filename_disk?: string
		}
	}
}

export const fields = [
	'anchor_title',
	'heading.text',
	'heading.title',
	'text.text',
	'button.href',
	'button.label',
	'image.alt',
	'image.image_file.image.id',
	'image.image_file.image.filename_disk',
	'options_page',
	'video.embed_url',
	'video.embed_title',
	'video.photo.id',
	'video.photo.filename_disk',
] as const

// Helper functions for YouTube handling (support full URL or plain ID)
const getYouTubeThumbnailUrl = (
	videoId: string,
	quality: 'default' | 'hq' | 'maxres' = 'maxres'
): string => {
	if (quality === 'default') {
		return `https://img.youtube.com/vi/${videoId}/0.jpg`
	} else if (quality === 'hq') {
		return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`
	} else {
		return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`
	}
}

const getVideoIdFromEmbedUrl = (embedUrl: string): string | null => {
	// If it's already just an ID (no slashes and no http), return it
	if (!embedUrl.includes('/') && !embedUrl.includes('http')) {
		return embedUrl
	}
	// Try to extract from typical YouTube embed URL formats
	const embedMatch = embedUrl.match(/\/embed\/([^?]+)/)
	if (embedMatch) return embedMatch[1]
	// Fallbacks for watch URLs and short youtu.be
	const watchMatch = embedUrl.match(/[?&]v=([^&]+)/)
	if (watchMatch) return watchMatch[1]
	const shortMatch = embedUrl.match(/youtu\.be\/([^?]+)/)
	return shortMatch ? shortMatch[1] : null
}

const getFullEmbedUrl = (embedUrl: string): string => {
	// If it's already a full URL, return as is
	if (embedUrl.includes('http')) {
		return embedUrl
	}
	// Otherwise treat as a YouTube ID
	return `https://www.youtube.com/embed/${embedUrl}`
}

interface ImageSplitDarkTextProps {
	data?: ImageSplitDarkTextData
}

export default function ImageSplitDarkText({ data }: ImageSplitDarkTextProps) {

	const [isPlaying, setIsPlaying] = useState(false)
	const isReversed = data?.options_page?.includes('flex-row-reverse')

	const { ref, isVisible } = useScrollAnimation({
		threshold: 0.05,
		rootMargin: '0px',
	})
	const sectionIdBase = data?.anchor_title || 'ImageSplitDarkText'
	const sectionId = sectionIdBase
		.replace(/\s+/g, '-')
		.replaceAll(/[^a-zA-Z0-9-_]/g, '-')
	
	const embedUrl = data?.video?.embed_url ? getFullEmbedUrl(data.video.embed_url) : ''
	const videoId = data?.video?.embed_url ? getVideoIdFromEmbedUrl(data.video.embed_url) : null
	const videoThumbnailUrl =
		data?.video?.photo?.id && data?.video?.photo?.filename_disk
			? getReadableImageUrl(data.video.photo.id, data.video.photo.filename_disk)
			: videoId
				? getYouTubeThumbnailUrl(videoId)
				: ''

	return (
		<section id={sectionId} data-name={data?.anchor_title || 'ImageSplitDarkText'} ref={ref} className='ImageSplitDarkText md:py-16 max-md:overflow-hidden'>
			<div className='container max-md:px-0'>
				<div
					className={`flex flex-col lg:flex-row md:gap-12 ${
						isReversed ? 'lg:flex-row-reverse' : ''
					}`}
				>
					{/* Left Column - Video or Image */}
					<FadeIn
						isVisible={isVisible}
						delay={200}
						direction='left'
						className='w-full lg:w-1/2'
					>
						<div className='w-full h-full max-md:aspect-[808/711] min-h-[300px] lg:min-h-[400px] relative bg-[#f3f1f1] overflow-hidden'>
							{data?.video ? (
								<div className='w-full h-full'>
									{videoId ? (
										isPlaying ? (
											<iframe
												src={`${embedUrl}?autoplay=1`}
												title={data.video.embed_title}
												className='w-full h-full absolute inset-0'
												frameBorder='0'
												allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
												allowFullScreen
											/>
										) : (
											<div
												className='relative w-full h-full cursor-pointer group'
												onClick={() => setIsPlaying(true)}
											>
												<Image
													src={videoThumbnailUrl || getYouTubeThumbnailUrl(videoId)}
													alt={data.video.embed_title || 'Video thumbnail'}
													fill
													className='object-cover group-hover:scale-105 transition-transform duration-300'
													unoptimized
													priority={false}
												/>
												<div className='absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/30 transition-colors'>
													<div className='group-hover:scale-110 transition-all duration-300'>
														<svg
															width='68'
															height='48'
															viewBox='0 0 68 48'
															className='drop-shadow-lg md:w-[68px] w-[40px]'
														>
															<path
																className='fill-red-600 group-hover:fill-red-700'
																d='M66.52,7.74c-0.78-2.93-2.49-5.41-5.42-6.19C55.79,.13,34,0,34,0S12.21,.13,6.9,1.55 C3.97,2.33,2.27,4.81,1.48,7.74C0.06,13.05,0,24,0,24s0.06,10.95,1.48,16.26c0.78,2.93,2.49,5.41,5.42,6.19 C12.21,47.87,34,48,34,48s21.79-0.13,27.1-1.55c2.93-0.78,4.64-3.26,5.42-6.19C67.94,34.95,68,24,68,24S67.94,13.05,66.52,7.74z'
															/>
															<path className='fill-white' d='M 45,24 27,14 27,34' />
														</svg>
													</div>
												</div>
											</div>
										)
									) : (
										<iframe
											src={embedUrl}
											title={data.video.embed_title}
											className='w-full h-full absolute inset-0'
											frameBorder='0'
											allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
											allowFullScreen
										/>
									)}
								</div>
							) : data?.image?.image_file?.image?.id ? (
								<Image
									src={getReadableImageUrl(
										data?.image?.image_file?.image?.id || '',
										data?.image?.image_file?.image?.filename_disk || ''
									)}
									alt={data.image.alt || 'Image'}
									fill
									className='object-cover'
									sizes='(max-width: 768px) 100vw, 50vw'
								/>
							) : (
								<div className='w-full h-full flex items-center justify-center text-gray-400'>
									No media available
								</div>
							)}
						</div>
					</FadeIn>

					{/* Right Column - Dark Text Block */}
					<FadeIn
						isVisible={isVisible}
						delay={0}
						direction='right'
						className='w-full lg:w-1/2'
					>
						<div className='w-full h-full p-6 lg:p-9 bg-[#625959]'>
							{/* Heading */}
							{(data?.heading?.title || data?.heading?.text) && (
								<h2
									className='text-white'
									dangerouslySetInnerHTML={sanitizeHTML(
										data?.heading?.title || data?.heading?.text || ''
									)}
								/>
							)}

							{/* Text */}
							{data?.text?.text && (
								<div
									className='text-white grid gap-4'
									dangerouslySetInnerHTML={sanitizeHTML(data.text.text)}
								/>
							)}

							{/* Button */}
							{data?.button?.href && (
								<div>
									<Link href={data.button.href} className='btn mt-6'>
										{data.button.label}
									</Link>
								</div>
							)}
						</div>
					</FadeIn>
				</div>
			</div>
		</section>
	)
}

